import moize from "moize";
import * as React from "react";
import Checkbox from "../../Inputs/Checkbox/Checkbox";

type Props<T> = {
	/**
	 * All of the possible matrix cells.
	 */
	options: T[];

	/**
	 * The cells which are currently selected.
	 * These do not need to have object-equality with the options -
	 * two cells are considered equal if their mapped column and row
	 * values are the same.
	 */
	selection: T[];

	/**
	 * Callback for notifying a change in cell selection.
	 */
	setSelection: (options: T[]) => void;

	/**
	 * A function mapping a cell to a string representing its column.
	 */
	optionToColumn: (option: T) => string;

	/**
	 * A function mapping a cell to a string representing its row.
	 */
	optionToRow: (option: T) => string;

	renderColumn: (columnName: string) => JSX.Element;

	onBlur?: () => void;

	needsFocus: boolean;
	acknowledgeFocusRequest: () => void;
	columnButtonContainsFocusableElement?: true;
};

/**
 * A view for editing a generic two dimensional array.
 */
export class MatrixEditor<T> extends React.PureComponent<Props<T>> {
	private readonly firstCheckbox: React.RefObject<Checkbox>;

	/**
	 * Toggle whether every cell in the column is selected.
	 * Memoized for performance.
	 */
	private readonly toggleColumn = moize((column: string) => () => {
		let selection: T[];

		if (
			this.props.options.every(
				o =>
					this.props.optionToColumn(o) !== column ||
					this.cellIsSelected(this.props.optionToColumn(o), this.props.optionToRow(o))
			)
		) {
			// Every cell in the column is already selected
			selection = this.props.selection.filter(o => this.props.optionToColumn(o) !== column);
		} else {
			selection = this.props.options.filter(
				o =>
					this.cellIsSelected(this.props.optionToColumn(o), this.props.optionToRow(o)) ||
					this.props.optionToColumn(o) === column
			);
		}

		this.props.setSelection(selection);
	});

	/**
	 * Toggle whether every cell in the row is selected.
	 * Memoized for performance.
	 */
	private readonly toggleRow = moize((row: string) => () => {
		let selection: T[];

		if (
			this.props.options.every(
				o =>
					this.props.optionToRow(o) !== row ||
					this.cellIsSelected(this.props.optionToColumn(o), this.props.optionToRow(o))
			)
		) {
			// Every cell in the row is already selected
			selection = this.props.selection.filter(o => this.props.optionToRow(o) !== row);
		} else {
			selection = this.props.options.filter(
				o =>
					this.cellIsSelected(this.props.optionToColumn(o), this.props.optionToRow(o)) ||
					this.props.optionToRow(o) === row
			);
		}

		this.props.setSelection(selection);
	});

	private readonly selectCell = moize((column: string, row: string) => (isChecked: boolean) => {
		let selection = this.props.selection;

		if (isChecked) {
			selection = selection.concat([this.cellToOption(column, row)]);
		} else {
			selection = selection.filter(o => !this.cellIsOption(o, column, row));
		}
		this.props.setSelection(selection);

		if (this.props.onBlur) {
			this.props.onBlur();
		}
	});
	constructor(props: Props<T>) {
		super(props);
		this.firstCheckbox = React.createRef();
	}

	public componentDidMount() {
		if (this.props.needsFocus && this.firstCheckbox.current) {
			this.firstCheckbox.current.focus();
			this.props.acknowledgeFocusRequest();
		}
	}

	public componentDidUpdate(prevProps: Props<T>) {
		if (!prevProps.needsFocus && this.props.needsFocus && this.firstCheckbox.current) {
			this.firstCheckbox.current.focus();
			this.props.acknowledgeFocusRequest();
		}
	}

	public render() {
		const rows = this.extractRows(this.props.options);
		const columns = this.extractColumns(this.props.options);

		return (
			<div className="matrix">
				<table>
					<thead>
						<tr>
							<th className="table-heading" />
							{columns.map((column, i) => {
								return (
									<th className="table-heading" key={i}>
										<button
											tabIndex={this.props.columnButtonContainsFocusableElement ? -1 : 0}
											type="button"
											className="btn__links matrix__button"
											onClick={this.toggleColumn(column)}
										>
											{this.props.renderColumn(column)}
										</button>
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rowIndex) => (
							<tr key={rowIndex}>
								<th>
									<button
										type="button"
										className="btn__links matrix__button"
										onClick={this.toggleRow(row)}
									>
										{row}
									</button>
								</th>

								{columns.map((column, idx) => (
									<td key={idx}>
										<span className="matrix__item">
											{this.props.options.some(o => this.cellIsOption(o, column, row)) && (
												<Checkbox
													label={`${column}/${row}`}
													value={`${column}/${row}`}
													name={`${column}/${row}`}
													checked={this.cellIsSelected(column, row)}
													onChange={this.selectCell(column, row)}
													ref={rowIndex === 0 && idx === 0 ? this.firstCheckbox : null}
												/>
											)}
										</span>
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	private readonly extractRows = (options: T[]) =>
		options.reduce(
			(rows, option) => {
				const optionRow = this.props.optionToRow(option);
				return rows.some(r => r === optionRow) ? rows : rows.concat(optionRow);
			},
			[] as string[]
		);

	private readonly extractColumns = (options: T[]) =>
		options.reduce(
			(columns, option) => {
				const optionCol = this.props.optionToColumn(option);
				return columns.some(c => c === optionCol) ? columns : columns.concat(optionCol);
			},
			[] as string[]
		);

	private readonly cellIsOption = (option: T, column: string, row: string) =>
		this.props.optionToColumn(option) === column && this.props.optionToRow(option) === row;

	private readonly cellIsSelected = (column: string, row: string) =>
		this.props.selection.some(o => this.cellIsOption(o, column, row));

	private readonly cellToOption = (column: string, row: string) => {
		const option = this.props.options.find(o => this.cellIsOption(o, column, row));
		if (option == null) {
			throw new Error("Could not find option for cell");
		}
		return option;
	};
}
